import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Radio,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronDown, ChevronUp, ListFilter, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import BadgeBoxCst from "@components/BadgeBoxCst";
import InputCst from "@components/InputCst/InputCst";
import PaginatorCst from "@components/PaginatorCst";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
import { useNormalizedAggregatedItems } from "@pages/Contents/V2/Corporate/Catalog/hooks/useNormalizedAggregatedItems";
import SearchFiltersAccordion from "@pages/Contents/V2/Corporate/Catalog/search/SearchFiltersAccordion";
import {
  buildSearchConditions,
  LO_SORT_ORDER_BY,
  SEARCH_FILTER_ORDER_BY_TAB,
} from "@pages/Contents/V2/Corporate/Catalog/search/types";
import { useLOEntitiesByIds } from "@pages/Contents/V2/Corporate/Catalog/search/useLOEntitiesByIds";
import { useLOFilterableFields } from "@pages/Contents/V2/Corporate/Catalog/search/useLOFilterableFields";
import { useLOSearch } from "@pages/Contents/V2/Corporate/Catalog/search/useLOSearch";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { Toggle } from "src/UI/Toggle";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const FILTER_FIELD_ORDER = SEARCH_FILTER_ORDER_BY_TAB.all.filter(
  (field) => field !== "standalone",
);
const HIDDEN_STATUS_VALUES = ["draft"];

export interface ReplacedCourse {
  id: number;
  title: string;
  entityType?: CatalogEntityType;
}

interface ReplacedCourseFieldProps {
  corporateId: string;
  label: string;
  enabled: boolean;
  selected: ReplacedCourse | null;
  disabled?: boolean;
  error?: string;
  onToggle: (next: boolean) => void;
  onSelect: (next: ReplacedCourse | null) => void;
}

const hasFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object")
    return Object.values(value as Record<string, unknown>).some(hasFilterValue);
  return true;
};

const ReplacedCourseField: React.FC<ReplacedCourseFieldProps> = ({
  corporateId,
  label,
  enabled,
  selected,
  disabled = false,
  error,
  onToggle,
  onSelect,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("skillBook-page");
  const [open, setOpen] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const isSearchActive = enabled && !disabled;

  const { fields: filterableFields } = useLOFilterableFields(
    corporateId,
    isSearchActive,
  );

  const conditions = useMemo(
    () =>
      buildSearchConditions({
        includeDrafts: false,
        standaloneOnly: true,
        fields: filterableFields,
        filterValues: filters,
      }),
    [filterableFields, filters],
  );

  const { items, totalItems, totalPages, isLoading } = useLOSearch({
    corporateId,
    conditions,
    searchText: search,
    orderBy: LO_SORT_ORDER_BY["id-desc"],
    page,
    pageSize,
    enabled: isSearchActive && !!corporateId,
  });
  const normalizedItems = useNormalizedAggregatedItems(items);

  const selectedId = selected?.id;
  const hasSelectedTitle = !!selected?.title;
  const lookupIds = useMemo(
    () => (selectedId !== undefined && !hasSelectedTitle ? [selectedId] : []),
    [selectedId, hasSelectedTitle],
  );
  const { entitiesById } = useLOEntitiesByIds({
    corporateId,
    ids: lookupIds,
    enabled: lookupIds.length > 0,
  });

  const hasActiveFilters = Object.values(filters).some(hasFilterValue);

  const resolvedSelected = useMemo(() => {
    if (!selected || selected.title) return selected;
    const found =
      normalizedItems.find(({ entity }) => Number(entity.id) === selected.id) ??
      entitiesById.get(selected.id);
    return found
      ? {
          id: selected.id,
          title: found.entity.title || found.entity.name || "",
          entityType: found.entityType,
        }
      : selected;
  }, [selected, normalizedItems, entitiesById]);

  const titleOf = (item: ReplacedCourse) =>
    item.title || t("replaced-course.id-only", { id: item.id });

  const handleToggle = (next: boolean) => {
    onToggle(next);
    if (!next) onSelect(null);
  };

  const textSx = { fontSize: "14px", color: theme.palette.text.primary };

  const renderRow = (item: ReplacedCourse, interactive: boolean) => {
    const isSelected = selected?.id === item.id;
    return (
      <Box
        key={`${item.entityType}-${item.id}`}
        onClick={interactive ? () => onSelect(item) : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          padding: "16px 20px",
          borderRadius: "12px",
          cursor: interactive ? "pointer" : "default",
          backgroundColor: theme.palette.background.card,
          border: `1px solid ${
            isSelected && interactive
              ? theme.palette.text.primary
              : "transparent"
          }`,
        }}
      >
        <Radio
          checked={isSelected}
          disabled={!interactive}
          onChange={interactive ? () => onSelect(item) : undefined}
          sx={{
            color: theme.palette.divider,
            "&.Mui-checked": { color: theme.palette.primary.main },
            padding: "4px",
          }}
        />
        <Divider orientation="vertical" flexItem />
        <Typography
          sx={{
            ...textSx,
            flex: 1,
            minWidth: 0,
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {titleOf(item)}
        </Typography>
        {item.entityType && (
          <BadgeBoxCst variant="info">
            {t(`replaced-course.types.${item.entityType}`)}
          </BadgeBoxCst>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {label}
        </Typography>
        <Toggle
          id="replaced-course-toggle"
          checked={enabled}
          onChange={handleToggle}
          disabled={disabled}
          readOnly={disabled}
          noLabel={t("replaced-course.no")}
          yesLabel={t("replaced-course.yes")}
          aria-label={label}
        />
      </Box>

      {enabled && disabled && (
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "16px", color: theme.palette.text.primary }}
          >
            {t("replaced-course.selected-title")}
          </Typography>
          {resolvedSelected ? (
            renderRow(resolvedSelected, false)
          ) : (
            <Typography sx={textSx}>{t("replaced-course.empty")}</Typography>
          )}
        </Box>
      )}

      {enabled && !disabled && (
        <Box
          sx={{
            border: `1px solid ${
              error ? theme.palette.error.main : theme.palette.divider
            }`,
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "16px", color: theme.palette.text.primary }}
              >
                {t("replaced-course.select-title")}
              </Typography>
              {!open && resolvedSelected && (
                <Typography sx={{ ...textSx, fontWeight: 700 }}>
                  {titleOf(resolvedSelected)}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpen((prev) => !prev)}
              aria-label={t("replaced-course.select-title") || ""}
              sx={{ color: theme.palette.text.primary }}
            >
              {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </IconButton>
          </Box>

          <Collapse in={open}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: "180px",
                    "& .MuiFormHelperText-root": { display: "none" },
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  <InputCst
                    id="replaced-course-search"
                    placeholder={t("replaced-course.search-placeholder") || ""}
                    inputSearch
                    value={searchInput}
                    onChange={(e: any) => {
                      setSearchInput(e.target.value);
                      setPage(1);
                    }}
                    startAdornment={
                      <Search
                        size={16}
                        color={theme.palette.systemSecondary01}
                        style={{ marginRight: 8 }}
                      />
                    }
                  />
                </Box>
                <ButtonSecondary
                  size="md"
                  startIcon={<ListFilter size={16} />}
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  sxButton={
                    hasActiveFilters || filtersOpen
                      ? { borderColor: theme.palette.systemSecondary01 }
                      : undefined
                  }
                >
                  {t("replaced-course.filters")}
                </ButtonSecondary>
              </Box>

              <SearchFiltersAccordion
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                fields={filterableFields}
                fieldOrder={FILTER_FIELD_ORDER}
                hiddenStatusValues={HIDDEN_STATUS_VALUES}
                value={filters}
                onApply={(next) => {
                  setFilters(next);
                  setPage(1);
                }}
                columns={2}
              />

              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : normalizedItems.length === 0 ? (
                <Typography sx={{ ...textSx, py: 2, textAlign: "center" }}>
                  {t("replaced-course.empty")}
                </Typography>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {normalizedItems.map(({ entity, entityType }) =>
                    renderRow(
                      {
                        id: Number(entity.id),
                        title: entity.title || entity.name || `#${entity.id}`,
                        entityType,
                      },
                      true,
                    ),
                  )}
                </Box>
              )}

              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <PaginatorCst
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    onPageChange={setPage}
                    onPageSizeChange={(next) => {
                      setPageSize(next);
                      setPage(1);
                    }}
                  />
                </Box>
              )}
            </Box>
          </Collapse>

          {error && (
            <Typography
              sx={{ fontSize: "12px", color: theme.palette.error.main }}
            >
              {error}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReplacedCourseField;
